import styled, { DefaultTheme } from 'styled-components';
import { Card, Table, Button, Input, Typography } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(
  props: { theme?: DefaultTheme },
  darkValue: T,
  lightValue: T
): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  width: 100%;
  padding: 16px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  background: ${(props) => getThemeValue(props, '#1A202C', '#F7FAFC')} !important;
  box-sizing: border-box;

  .ant-typography {
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

export const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StyledCard = styled(Card)`
  background-color: ${(props) =>
    getThemeValue(props, '#2D3748', '#ffffff')};
  border-color: ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 12px rgba(0, 0, 0, 0.3)',
      '0 2px 8px rgba(0, 0, 0, 0.1)'
    )};
  border-radius: 8px;

  .ant-card-head {
    background-color: ${(props) =>
      getThemeValue(props, '#2D3748', '#ffffff')};
    border-bottom-color: ${(props) =>
      getThemeValue(props, '#4A5568', '#E2E8F0')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  }

  .ant-card-body {
    background-color: ${(props) =>
      getThemeValue(props, '#2D3748', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  }

  .ant-card-head-title {
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  }
`;

export const SummaryCard = styled(StyledCard)<{ borderColor?: string }>`
  border-left: 4px solid
    ${(props) => props.borderColor || getThemeValue(props, '#1890ff', '#1890ff')};
`;

export const PeriodCard = styled(StyledCard)`
  margin-bottom: 16px;
`;

export const StyledTable = styled(Table)`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 12px rgba(0, 0, 0, 0.3)',
      '0 2px 8px rgba(0, 0, 0, 0.1)'
    )};

  .ant-table {
    background-color: ${(props) =>
      getThemeValue(props, '#2D3748', '#ffffff')};
    border-radius: 8px;
  }

  .ant-table-container {
    border-radius: 8px;
  }

  .ant-table-thead > tr > th {
    background-color: ${(props) =>
      getThemeValue(props, '#4A5568', '#fafafa')};
    border-bottom: 2px solid
      ${(props) => getThemeValue(props, '#718096', '#e8e8e8')};
    color: ${(props) => getThemeValue(props, '#ffffff', 'rgba(0, 0, 0, 0.85)')} !important;
    font-weight: 600;
    font-size: 14px;
    padding: 16px 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    &:first-child {
      border-top-left-radius: 8px;
    }
    
    &:last-child {
      border-top-right-radius: 8px;
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid
      ${(props) => getThemeValue(props, '#4A5568', '#f0f0f0')};
    color: ${(props) => getThemeValue(props, '#ffffff', 'rgba(0, 0, 0, 0.85)')} !important;
    padding: 16px 12px;
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: ${(props) =>
      getThemeValue(props, '#4A5568', '#f5f5f5')};
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
    
    &:first-child {
      border-bottom-left-radius: 8px;
    }
    
    &:last-child {
      border-bottom-right-radius: 8px;
    }
  }

  .ant-spin-nested-loading {
    .ant-spin-container {
      .ant-table {
        border-radius: 8px;
      }
    }
  }

  .ant-table-placeholder {
    .ant-empty {
      margin: 40px 0;
    }
  }

  /* Loading spinner styling */
  .ant-spin-spinning {
    .ant-spin-dot {
      i {
        background-color: ${(props) =>
          getThemeValue(props, '#1890ff', '#1890ff')};
      }
    }
  }
`;

export const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

export const PeriodsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const PeriodHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const PeriodFields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

export const ActionButton = styled(Button)`
  border-radius: 6px;
  background-color: #2d6cca !important;
  border-color: #2d6cca !important;
  color: #ffffff !important;

  &:hover {
    background-color: #1e5bb8 !important;
    border-color: #1e5bb8 !important;
    color: #ffffff !important;
  }

  &:disabled {
    background-color: #2d6cca !important;
    border-color: #2d6cca !important;
    color: #ffffff !important;
    opacity: 0.5;
  }
`;

export const StyledButton = styled(Button)`
  border-radius: 8px !important;
  padding: 14px 8px !important;
  border: 0 !important;
  background-color: #2d6cca !important;
  color: #ffffff !important;

  &:hover {
    background-color: #1e5bb8 !important;
    color: #ffffff !important;
  }

  &:disabled {
    background-color: #2d6cca !important;
    color: #ffffff !important;
    opacity: 0.5;
  }
`;

export const StyledInput = styled(Input)`
  border-radius: 6px;
  background-color: ${(props) =>
    getThemeValue(props, '#2D3748', '#ffffff')} !important;
  border-color: ${(props) => getThemeValue(props, '#4A5568', '#d9d9d9')} !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  
  &::placeholder {
    color: ${(props) => getThemeValue(props, '#718096', '#bfbfbf')} !important;
  }
  
  &:hover {
    border-color: ${(props) => getThemeValue(props, '#718096', '#40a9ff')} !important;
  }
  
  &:focus {
    border-color: ${(props) => getThemeValue(props, '#1890ff', '#40a9ff')} !important;
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 0 0 2px rgba(24, 144, 255, 0.2)',
        '0 0 0 2px rgba(24, 144, 255, 0.2)'
      )} !important;
  }
`;

export const StyledDateInput = styled(Input)`
  border-radius: 6px;
  background-color: ${(props) =>
    getThemeValue(props, '#2D3748', '#ffffff')} !important;
  border-color: ${(props) => getThemeValue(props, '#4A5568', '#d9d9d9')} !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  
  &::placeholder {
    color: ${(props) => getThemeValue(props, '#718096', '#bfbfbf')} !important;
  }
  
  &:hover {
    border-color: ${(props) => getThemeValue(props, '#718096', '#40a9ff')} !important;
  }
  
  &:focus {
    border-color: ${(props) => getThemeValue(props, '#1890ff', '#40a9ff')} !important;
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 0 0 2px rgba(24, 144, 255, 0.2)',
        '0 0 0 2px rgba(24, 144, 255, 0.2)'
      )} !important;
  }
  
  &::-webkit-calendar-picker-indicator {
    filter: ${(props) =>
      props.theme?.mode === 'dark' ? 'invert(1)' : 'none'};
  }
`;

export const StatValue = styled(Typography.Text)<{ color?: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => props.color || 'inherit'};
`;

export const StatLabel = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
`;

export const ComparisonCard = styled(StyledCard)`
  .ant-card-body {
    padding: 20px;
  }
`;

export const ComparisonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

export const ComparisonTitle = styled(Typography.Title)`
  margin: 0 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const ComparisonDate = styled(Typography.Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
`;

export const ComparisonStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const ComparisonStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ComparisonGrowth = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid
    ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};
`;

export const GrowthValue = styled(Typography.Text)<{ trend?: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => {
    if (props.trend === 'up') return '#52c41a';
    if (props.trend === 'down') return '#ff4d4f';
    return '#8c8c8c';
  }};
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  gap: 16px;
`;

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  color: ${(props) => getThemeValue(props, '#718096', '#A0AEC0')} !important;
`;

export const EmptyStateTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const EmptyStateText = styled(Typography.Text)`
  text-align: center;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
`;

export const BackButtonWrapper = styled.div`
  margin-right: 16px;
`;

const { Title: AntTitle, Text: AntText } = Typography;

export const TitleNoMargin = styled(AntTitle)`
  margin: 0 !important;
`;

export const LabelText = styled(AntText)`
  display: block;
  margin-bottom: 8px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const SecondaryLabelText = styled(AntText)`
  display: block;
  margin-bottom: 4px;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
`;

export const PeriodTitleText = styled(AntText)`
  display: block;
  margin-bottom: 12px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const PeriodCardWrapper = styled(Card)`
  margin-bottom: 16px;
  background-color: ${(props) =>
    getThemeValue(props, '#2D3748', '#ffffff')} !important;
  border-color: ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')} !important;
  
  .ant-card-body {
    background-color: ${(props) =>
      getThemeValue(props, '#2D3748', '#ffffff')} !important;
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  }
  
  .ant-card-extra {
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  }
`;

export const ActionButtonWrapper = styled.div`
  text-align: center;
  margin-top: 24px;
`;

export const ComparisonTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ComparisonCardWrapper = styled(StyledCard)`
  margin-top: 24px;
`;

export const MobileSpace = styled.div`
  width: 100%;
  
  .ant-space {
    width: 100%;
  }
`;


export const RightAlignedStat = styled(ComparisonStat)`
  text-align: right;
`;

export const AlertWrapper = styled.div`
  margin-bottom: 16px;
`;

export const TrendIcon = styled.span<{ color: string }>`
  color: ${(props) => props.color};
`;

export const TableText = styled.span<{ fontWeight?: number; color?: string }>`
  font-weight: ${(props) => props.fontWeight || 600};
  color: ${(props) => props.color || 'inherit'};
`;
